import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { accessLocalStorage } from '../../../services';
import callInactivityApi from '../../../services/callInactivityApi';

import Loading from '../../Loading/LoadingModule';
import TimeInput24 from './TimeInput24';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSave, 
  faTrash, 
  faBell, 
  faClock,
  faEnvelope,
  faCalendarAlt,
  faToggleOn,
  faToggleOff,
  faPlus
} from '@fortawesome/free-solid-svg-icons';

import '../../../libs/bulma.min.css';
import './CallInactivityConfig.css';

function CallInactivityConfig() {
  const history = useHistory();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState({ show: false, type: '', message: '' });
  const [hasConfig, setHasConfig] = useState(false);
  const [userTenant, setUserTenant] = useState('');

  const [formData, setFormData] = useState({
    enabled: false,
    monitored_call_type: 'both',
    inactivity_period_hours: 24,
    alert_email: '',
    timezone: 'America/Sao_Paulo'
  });

  const [schedules, setSchedules] = useState([
    {
      days_of_week: '1,2,3,4,5',
      start_time: '08:00',
      end_time: '18:00',
      description: 'Horário Comercial'
    }
  ]);

  const showAlert = (type, message) => {
    setAlert({ show: true, type, message });
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => {
      setAlert({ show: false, type: '', message: '' });
    }, 5000);
  };

  const loadConfig = async (tenant) => {
    try {
      const response = await callInactivityApi.getConfig(tenant);
      
      if (response.data) {
        setFormData({
          enabled: response.data.enabled,
          monitored_call_type: response.data.monitored_call_type,
          inactivity_period_hours: response.data.inactivity_period_hours,
          alert_email: response.data.alert_email,
          timezone: response.data.timezone || 'America/Sao_Paulo'
        });
        
        if (response.data.schedules && response.data.schedules.length > 0) {
          const formattedSchedules = response.data.schedules.map(s => ({
            days_of_week: s.days_of_week,
            start_time: s.start_time.substring(0, 5),
            end_time: s.end_time.substring(0, 5),
            description: s.description || ''
          }));
          setSchedules(formattedSchedules);
        }
        
        setHasConfig(true);
      } else {
        setHasConfig(false);
      }
    } catch (error) {
      console.error('Erro ao carregar configuração:', error);
    }
  };

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const user = await accessLocalStorage.getUserLocalStorage();
        if (!user?.user?.idtenant) {
          history.push('/');
          return;
        }

        const tenant = user.user.idtenant;
        console.log('✅ [CALL-INACTIVITY-CONFIG] Tenant identificado:', tenant);
        setUserTenant(tenant);
        
        await loadConfig(tenant);
      } catch (error) {
        console.error('Erro ao carregar dados iniciais:', error);
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleScheduleChange = (index, field, value) => {
    const newSchedules = [...schedules];
    
    if (field === 'start_time' || field === 'end_time') {
      let timeValue = value;
      if (value && value.includes(':')) {
        const parts = value.split(':');
        const hours = parseInt(parts[0]);
        const minutes = parts[1];
        
        if (hours >= 0 && hours <= 23) {
          timeValue = `${hours.toString().padStart(2, '0')}:${minutes}`;
        } else {
          return;
        }
      }
      
      newSchedules[index] = {
        ...newSchedules[index],
        [field]: timeValue
      };
    } else {
      newSchedules[index] = {
        ...newSchedules[index],
        [field]: value
      };
    }
    
    setSchedules(newSchedules);
  };

  const handleDayToggle = (scheduleIndex, day) => {
    const schedule = schedules[scheduleIndex];
    const daysArray = schedule.days_of_week.split(',').map(d => parseInt(d.trim()));
    
    let newDaysArray;
    if (daysArray.includes(day)) {
      newDaysArray = daysArray.filter(d => d !== day);
    } else {
      newDaysArray = [...daysArray, day].sort((a, b) => a - b);
    }
    
    if (newDaysArray.length === 0) {
      showAlert('warning', 'Selecione pelo menos um dia');
      return;
    }
    
    handleScheduleChange(scheduleIndex, 'days_of_week', newDaysArray.join(','));
  };

  const addSchedule = () => {
    setSchedules([...schedules, {
      days_of_week: '1,2,3,4,5',
      start_time: '08:00',
      end_time: '18:00',
      description: ''
    }]);
    window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
  };

  const removeSchedule = (index) => {
    if (schedules.length === 1) {
      showAlert('warning', 'Deve haver pelo menos um horário configurado');
      return;
    }
    const newSchedules = schedules.filter((_, i) => i !== index);
    setSchedules(newSchedules);
  };

  const validateForm = () => {
    if (!formData.alert_email) {
      showAlert('warning', 'Email de alerta é obrigatório');
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.alert_email)) {
      showAlert('warning', 'Email inválido');
      return false;
    }

    if (formData.inactivity_period_hours < 1 || formData.inactivity_period_hours > 720) {
      showAlert('warning', 'Período de inatividade deve estar entre 1 e 720 horas');
      return false;
    }

    if (schedules.length === 0) {
      showAlert('warning', 'Adicione pelo menos um horário de monitoramento');
      return false;
    }

    for (const schedule of schedules) {
      if (!schedule.days_of_week || schedule.days_of_week.length === 0) {
        showAlert('warning', 'Todos os horários devem ter pelo menos um dia selecionado');
        return false;
      }
      if (!schedule.start_time || !schedule.end_time) {
        showAlert('warning', 'Todos os horários devem ter hora de início e fim');
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!userTenant) {
      showAlert('danger', 'Erro ao identificar o tenant. Recarregue a página.');
      return;
    }

    if (!validateForm()) {
      return;
    }

    setSaving(true);

    try {
      const data = {
        tenant_id: userTenant,
        enabled: formData.enabled,
        monitored_call_type: formData.monitored_call_type,
        inactivity_period_hours: parseInt(formData.inactivity_period_hours),
        alert_email: formData.alert_email,
        schedules: schedules.map(s => ({
          days_of_week: s.days_of_week,
          start_time: s.start_time + ':00',
          end_time: s.end_time + ':00',
          description: s.description || null
        })),
        timezone: formData.timezone
      };

      console.log('Enviando dados:', data);

      if (hasConfig) {
        await callInactivityApi.updateConfig(userTenant, data);
        showAlert('success', 'Configuração atualizada com sucesso!');
      } else {
        await callInactivityApi.saveConfig(data);
        showAlert('success', 'Configuração criada com sucesso!');
        setHasConfig(true);
      }

    } catch (error) {
      console.error('Erro ao salvar configuração:', error);
      const errorMsg = error.response?.data?.error || 'Erro ao salvar configuração';
      showAlert('danger', errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Tem certeza que deseja remover esta configuração? Todos os horários serão excluídos.')) {
      return;
    }

    setSaving(true);
    try {
      await callInactivityApi.deleteConfig(userTenant);
      setFormData({
        enabled: false,
        monitored_call_type: 'both',
        inactivity_period_hours: 24,
        alert_email: '',
        timezone: 'America/Sao_Paulo'
      });
      setSchedules([
        {
          days_of_week: '1,2,3,4,5',
          start_time: '08:00',
          end_time: '18:00',
          description: 'Horário Comercial'
        }
      ]);
      setHasConfig(false);
      showAlert('success', 'Configuração removida com sucesso!');
    } catch (error) {
      console.error('Erro ao remover configuração:', error);
      showAlert('danger', 'Erro ao remover configuração');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loading />;

  return (
    <div className="call-inactivity-form-container">
      <div className="call-inactivity-form-header">
        <div className="call-inactivity-form-title-wrapper">
          <svg className="call-inactivity-form-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
          </svg>
          <div>
            <h1 className="call-inactivity-form-title">Monitoramento de Inatividade</h1>
            <p className="call-inactivity-form-subtitle">Configure alertas automáticos de inatividade de chamadas</p>
          </div>
        </div>
      </div>

      <div className="alerts-section">
        {alert.show && (
          <div className={`alert-message alert-${alert.type}`}>
            <div className="alert-header">
              <span>{alert.type === 'success' ? 'Sucesso!' : alert.type === 'danger' ? 'Erro!' : 'Atenção!'}</span>
              <button className="alert-close" onClick={() => setAlert({ show: false, type: '', message: '' })}>×</button>
            </div>
            <div className="alert-body">{alert.message}</div>
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit}>
        <div className="form-section-card">
          <h3 className="form-section-title">
            <FontAwesomeIcon icon={faBell} className="section-icon" />
            Status do Monitoramento
          </h3>
          <div className="switch-container">
            <label className="switch">
              <input
                type="checkbox"
                name="enabled"
                checked={formData.enabled}
                onChange={handleInputChange}
              />
              <span className="slider"></span>
            </label>
            {formData.enabled ? (
              <span className="status-tag tag-enabled">
                <FontAwesomeIcon icon={faToggleOn} />
                Habilitado
              </span>
            ) : (
              <span className="status-tag tag-disabled">
                <FontAwesomeIcon icon={faToggleOff} />
                Desabilitado
              </span>
            )}
          </div>
          <p className="form-help">Ative para começar a receber alertas de inatividade</p>
        </div>

        <div className="form-section-card">
          <h3 className="form-section-title">Tipo de Chamadas Monitoradas</h3>
          <div className="radio-cards">
            <label className="radio-card">
              <input
                type="radio"
                name="monitored_call_type"
                value="inbound"
                checked={formData.monitored_call_type === 'inbound'}
                onChange={handleInputChange}
              />
              <div className="radio-card-content">
                <strong>Entrada</strong>
                <p>Chamadas recebidas</p>
              </div>
            </label>
            <label className="radio-card">
              <input
                type="radio"
                name="monitored_call_type"
                value="outbound"
                checked={formData.monitored_call_type === 'outbound'}
                onChange={handleInputChange}
              />
              <div className="radio-card-content">
                <strong>Saída</strong>
                <p>Chamadas realizadas</p>
              </div>
            </label>
            <label className="radio-card">
              <input
                type="radio"
                name="monitored_call_type"
                value="both"
                checked={formData.monitored_call_type === 'both'}
                onChange={handleInputChange}
              />
              <div className="radio-card-content">
                <strong>Ambas</strong>
                <p>Entrada e Saída</p>
              </div>
            </label>
          </div>
        </div>

        <div className="form-section-card">
          <h3 className="form-section-title">Configurações Gerais</h3>
          <div className="form-grid">
            <div className="form-field">
              <label className="form-label">
                <FontAwesomeIcon icon={faClock} className="mr-1" />
                Período de Inatividade (horas)
              </label>
              <input
                className="form-input"
                type="number"
                name="inactivity_period_hours"
                value={formData.inactivity_period_hours}
                onChange={handleInputChange}
                min="1"
                max="720"
                required
              />
              <p className="form-help">Tempo sem chamadas antes de enviar o alerta</p>
            </div>

            <div className="form-field">
              <label className="form-label">
                <FontAwesomeIcon icon={faEnvelope} className="mr-1" />
                Email para Alertas
              </label>
              <input
                className="form-input"
                type="email"
                name="alert_email"
                value={formData.alert_email}
                onChange={handleInputChange}
                placeholder="email@exemplo.com"
                required
              />
              <p className="form-help">Email que receberá os alertas</p>
            </div>
          </div>
        </div>

        <div className="form-section-card">
          <h3 className="form-section-title">
            <FontAwesomeIcon icon={faCalendarAlt} className="section-icon" />
            Horários de Monitoramento
            <span className="schedule-count">({schedules.length})</span>
          </h3>

          {schedules.map((schedule, index) => (
            <div key={index} className="schedule-card">
              <div className="schedule-header">
                <h4 className="schedule-title">
                  Horário {index + 1}
                  {schedule.description && <span className="schedule-desc"> - {schedule.description}</span>}
                </h4>
                {schedules.length > 1 && (
                  <button
                    type="button"
                    className="btn-remove-schedule"
                    onClick={() => removeSchedule(index)}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                    Remover
                  </button>
                )}
              </div>

              <div className="form-field">
                <label className="form-label">Descrição (opcional)</label>
                <input
                  className="form-input"
                  type="text"
                  value={schedule.description}
                  onChange={(e) => handleScheduleChange(index, 'description', e.target.value)}
                  placeholder="Ex: Horário Comercial, Plantão, Final de Semana"
                />
              </div>

              <div className="form-field">
                <label className="form-label">Dias da Semana</label>
                <div className="day-selector">
                  {[
                    { value: 0, label: 'Dom' },
                    { value: 1, label: 'Seg' },
                    { value: 2, label: 'Ter' },
                    { value: 3, label: 'Qua' },
                    { value: 4, label: 'Qui' },
                    { value: 5, label: 'Sex' },
                    { value: 6, label: 'Sáb' }
                  ].map(day => {
                    const daysArray = schedule.days_of_week.split(',').map(d => parseInt(d.trim()));
                    const isActive = daysArray.includes(day.value);
                    return (
                      <button
                        key={day.value}
                        type="button"
                        className={`day-button ${isActive ? 'active' : ''}`}
                        onClick={() => handleDayToggle(index, day.value)}
                      >
                        {day.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="form-grid">
                <div className="form-field">
                  <label className="form-label">
                    <FontAwesomeIcon icon={faClock} className="mr-1" />
                    Hora de Início (24h)
                  </label>
                  <TimeInput24
                    className="form-input"
                    value={schedule.start_time}
                    onChange={(newValue) => handleScheduleChange(index, 'start_time', newValue)}
                    required
                  />
                  <p className="form-help">Formato 24 horas (ex: 08:00)</p>
                </div>
                <div className="form-field">
                  <label className="form-label">
                    <FontAwesomeIcon icon={faClock} className="mr-1" />
                    Hora de Fim (24h)
                  </label>
                  <TimeInput24
                    className="form-input"
                    value={schedule.end_time}
                    onChange={(newValue) => handleScheduleChange(index, 'end_time', newValue)}
                    required
                  />
                  <p className="form-help">Formato 24 horas (ex: 18:00)</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="form-section-card">
          <div className="form-actions">
            {hasConfig && (
              <button
                type="button"
                className={`btn-delete ${saving ? 'is-loading' : ''}`}
                onClick={handleDelete}
                disabled={saving}
              >
                <FontAwesomeIcon icon={faTrash} />
                Remover Configuração
              </button>
            )}
            <button
              type="submit"
              className={`btn-submit ${saving ? 'is-loading' : ''}`}
              disabled={saving}
            >
              <FontAwesomeIcon icon={faSave} />
              {hasConfig ? 'Atualizar' : 'Salvar'} Configuração
            </button>
          </div>
        </div>
      </form>

      <button
        type="button"
        className="btn-floating-add"
        onClick={addSchedule}
        title="Adicionar Horário"
      >
        <FontAwesomeIcon icon={faPlus} className="floating-icon" />
        <span className="floating-text">Adicionar Horário</span>
      </button>
    </div>
  );
}

export default CallInactivityConfig;
